import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export function SendReportEmail({ summary, period, dateFrom, dateTo }) {
  const { authAxios } = useAuth();
  const [email, setEmail] = useState("");
  const [selectedFormats, setSelectedFormats] = useState({
    pdf: false,
    xlsx: false,
    csv: false,
  });
  const [isSending, setIsSending] = useState(false);

  const validateEmail = (email) => {
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(email);
  };

  const handleSendEmail = async () => {
    if (!email.trim()) {
      toast.error("Ingresa un email");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Email inválido");
      return;
    }

    const selectedCount = Object.values(selectedFormats).filter(Boolean).length;
    if (selectedCount === 0) {
      toast.error("Selecciona al menos un formato");
      return;
    }

    setIsSending(true);

    try {
      const formats = Object.keys(selectedFormats).filter((k) => selectedFormats[k]);

      const payload = {
        email: email.trim(),
        period: period,
        date_from: period === "custom" ? dateFrom : null,
        date_to: period === "custom" ? dateTo : null,
        formats: formats,
      };

      console.log("[SendReportEmail] Payload enviado:", JSON.stringify(payload, null, 2));

      const response = await authAxios.post("/financiero/accounting-reports/send-email", payload);

      console.log("[SendReportEmail] Resposta recebida:", JSON.stringify(response.data, null, 2));

      if (response.data && response.data.status === "success") {
        const successMessage = String(response.data.message) || "Email enviado exitosamente";
        console.log("[SendReportEmail] Mensaje de éxito:", successMessage);
        toast.success(successMessage);
        setEmail("");
        setSelectedFormats({ pdf: false, xlsx: false, csv: false });
      } else {
        const errorMsg = String(response.data?.message || response.data?.error || "Error al enviar email");
        console.log("[SendReportEmail] Error en respuesta:", errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error("[SendReportEmail] Exception capturada:", err);

      let errorMessage = "Error al enviar email";

      if (err.response) {
        console.log("[SendReportEmail] Error response status:", err.response.status);
        console.log("[SendReportEmail] Error response data:", JSON.stringify(err.response.data, null, 2));

        if (typeof err.response.data === "string") {
          errorMessage = err.response.data;
        } else if (typeof err.response.data === "object" && err.response.data !== null) {
          errorMessage = String(err.response.data.detail || err.response.data.message || err.response.data.error || "Error desconocido");
        }
      } else if (err.message) {
        errorMessage = String(err.message);
      }

      console.log("[SendReportEmail] Mensaje de error final:", errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card className="border-purple-200 bg-purple-50">
      <CardHeader>
        <CardTitle className="text-base uppercase tracking-tight text-purple-900 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Enviar al Contabilista
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-semibold text-gray-700 block mb-2">
            Email del destinatario:
          </label>
          <input
            type="email"
            placeholder="contabilidad@empresa.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSending}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-gray-700 block mb-3">
            Tipo de archivo:
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFormats.pdf}
                onChange={(e) =>
                  setSelectedFormats({ ...selectedFormats, pdf: e.target.checked })
                }
                disabled={isSending}
                className="w-4 h-4 rounded border-gray-300 cursor-pointer disabled:cursor-not-allowed"
              />
              <span className="text-sm text-gray-700">PDF Ejecutivo</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFormats.xlsx}
                onChange={(e) =>
                  setSelectedFormats({ ...selectedFormats, xlsx: e.target.checked })
                }
                disabled={isSending}
                className="w-4 h-4 rounded border-gray-300 cursor-pointer disabled:cursor-not-allowed"
              />
              <span className="text-sm text-gray-700">Excel Detallado</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFormats.csv}
                onChange={(e) =>
                  setSelectedFormats({ ...selectedFormats, csv: e.target.checked })
                }
                disabled={isSending}
                className="w-4 h-4 rounded border-gray-300 cursor-pointer disabled:cursor-not-allowed"
              />
              <span className="text-sm text-gray-700">CSV</span>
            </label>
          </div>
        </div>

        <Button
          onClick={handleSendEmail}
          disabled={isSending}
          className="w-full gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed"
        >
          {isSending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Enviar al Contabilista
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
